import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import type {RenderSlideProps} from 'yet-another-react-lightbox';

import {
  useController,
  useLightboxProps,
  useLightboxState,
} from 'yet-another-react-lightbox';

import {LoadingSpinner} from '../../../../uikit/LoadingSpinner';
import {logger} from '../../../../utils/logger';
import {storageTokenManager} from '../../../../utils/storageToken';
import * as S from './styles';
import {isHtmlSrc} from './utils';

const getFileSize = async (url: string) => {
  try {
    const response = await fetch(url, {
      // no-cors is required to get blob
      mode: 'no-cors',
    });

    const blob = await response.blob();

    return blob.size;
  } catch (e) {
    logger.error(e);

    return 0;
  }
};

type Props = RenderSlideProps & {isActive: boolean};

const DEFAULT_RELOAD_TIMEOUT = 2000;

export const DocumentSlide = ({isActive, slide}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const lightboxState = useLightboxState();
  const lightboxProps = useLightboxProps();
  const controller = useController();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const reloadInterval = useRef(0);
  const reloadTimeout = useRef(DEFAULT_RELOAD_TIMEOUT);

  const isSingleSlide = lightboxState.slides.length === 1;

  const signedSrc = useMemo(() => {
    const src = storageTokenManager.signUrl(slide.src || '');

    if (isHtmlSrc(slide.src)) {
      return src;
    }

    return src;
  }, [slide.src]);

  const src = useMemo(
    () =>
      `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
        signedSrc,
      )}`,
    [signedSrc],
  );

  useEffect(() => {
    const setup = async () => {
      const fileSize = await getFileSize(signedSrc);

      // calc timeout based on file size in bytes with download speed 250kb/s
      const fileSizeInKb = fileSize / 1024;

      reloadTimeout.current = Math.max(
        DEFAULT_RELOAD_TIMEOUT,
        (fileSizeInKb / 250) * 1000,
      );

      setIsReady(true);
    };

    if (isActive) {
      setup();
    }
  }, [signedSrc, isActive]);

  const reload = useCallback(() => {
    if (!iframeRef.current) {
      return;
    }

    iframeRef.current.src += '';
  }, []);

  useEffect(() => {
    if (isReady) {
      reloadInterval.current = +setInterval(reload, reloadTimeout.current);
    }

    return () => {
      clearInterval(reloadInterval.current);
    };
  }, [isReady, reload]);

  const onContainerClick = () => {
    if (lightboxProps.controller.closeOnBackdropClick) {
      controller.close();
    }
  };

  const onLoad = useCallback(() => {
    setIsLoading(false);

    clearInterval(reloadInterval.current);
  }, []);

  return (
    <S.Document.Container isSingle={isSingleSlide} onClick={onContainerClick}>
      {isReady ? <iframe onLoad={onLoad} ref={iframeRef} src={src} /> : null}
      {isLoading ? (
        <S.Document.LoadingSpinnerContainer>
          <LoadingSpinner size={50} />
        </S.Document.LoadingSpinnerContainer>
      ) : null}
    </S.Document.Container>
  );
};
